import { useState, useEffect } from "react";

function APConvertions(min) {
  // console.log("MINUTES: ", min);
  if (min >= 1 && min <= 15) {
    return 15;
  } else if (min >= 16 && min <= 30) {
    return 30;
  } else if (min >= 31 && min <= 45) {
    return 45;
  } else if (min >= 46 && min <= 59) {
    return 60;
  } else {
    return min;
  }
}

function getLeaveAbbreviation(leaveType) {
  const LEAVE_TYPE_MAPPING = {
    "SOLO PARENT LEAVE": "SOLO",
    "STUDY LEAVE": "STUDY",
    "SPECIAL LEAVE BENEFITS FOR WOMEN": "MC",
    "SPECIAL EMERGENCY LEAVE": "CALAMITY",
  };

  return (
    LEAVE_TYPE_MAPPING[leaveType.toUpperCase()] ||
    leaveType
      .split(" ") // Split by space, hyphen, slash, or parentheses
      .filter((word) => word.length > 0) // Remove empty strings
      .map((word) => word[0].toUpperCase()) // Get first letter and uppercase
      .join("")
  );
}

const TimeTable = ({
  dailyRec = [],
  month,
  year,
  employee,
  watermark,
  lastDay,
  holidays = [],
  suspensions = [],
  leaves = [],
  manual_dtr = [],
  obLists = [],
}) => {
  const [holidayMap, setHolidayMap] = useState({});

  useEffect(() => {
    // Create a map of date -> status data from props
    const map = {};

    // Get the current month and year from dailyRec
    const parsedMonth = Number(month);
    const currentMonth = Number.isNaN(parsedMonth)
      ? new Date().getMonth()
      : parsedMonth;

    // dailyRec.length > 0 ? Number(month) : new Date().getMonth();
    const currentYear = Number(year) || new Date().getFullYear();
    // dailyRec.length > 0 ? Number(year) : new Date().getFullYear();

    // Helper function to get date string without timezone issues
    const getDateKey = (dateStr) => {
      // Extract YYYY-MM-DD directly from ISO string to avoid timezone conversion
      if (typeof dateStr === "string" && dateStr.includes("T")) {
        return dateStr.split("T")[0];
      }
      const d = new Date(dateStr);
      return d.toISOString().split("T")[0];
    };

    // Helper function to format date as YYYY-MM-DD using local time (not UTC)
    const formatDateLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    // Map holidays by date
    holidays.forEach((holiday) => {
      if (holiday.date) {
        const dateKey = getDateKey(holiday.date);
        const dateObj = new Date(dateKey + "T00:00:00"); // Parse as local date
        // Only include holidays from the current month/year
        if (
          dateObj.getMonth() === currentMonth &&
          dateObj.getFullYear() === currentYear
        ) {
          if (!map[dateKey]) map[dateKey] = {};
          map[dateKey].holiday = holiday;
        }
      }
    });

    // Map OB (Official Business) by date range
    obLists.forEach((ob) => {
      const selectedDates = ob?.selectedDate;
      if (!Array.isArray(selectedDates)) return;

      selectedDates.forEach((dateRanges) => {
        if (!Array.isArray(dateRanges)) return;

        dateRanges.forEach((range) => {
          if (!Array.isArray(range) || range.length !== 2) return;

          const [startDateStr, endDateStr] = range;

          const startDateObj = new Date(`${getDateKey(startDateStr)}T00:00:00`);
          const endDateObj = new Date(`${getDateKey(endDateStr)}T00:00:00`);

          if (isNaN(startDateObj) || isNaN(endDateObj)) return;

          for (
            let currentDate = new Date(startDateObj);
            currentDate <= endDateObj;
            currentDate.setDate(currentDate.getDate() + 1)
          ) {
            if (
              currentDate.getMonth() !== currentMonth ||
              currentDate.getFullYear() !== currentYear
            )
              continue;

            const dateKey = formatDateLocal(currentDate);
            map[dateKey] ??= {};
            map[dateKey].ob = ob;
          }
        });
      });
    });

    // Map suspensions by date range
    suspensions.forEach((suspension) => {
      if (suspension.dateFrom && suspension.dateTo) {
        const startKey = getDateKey(suspension.dateFrom);
        const endKey = getDateKey(suspension.dateTo);
        const startDate = new Date(startKey).toISOString().split("T")[0];
        const endDate = new Date(endKey).toISOString().split("T")[0];

        for (
          let d = new Date(startDate);
          d.getDate() <= new Date(endDate).getDate();
          d.setDate(d.getDate() + 1)
        ) {
          // Only include suspensions in the current month/year
          if (
            d.getMonth() === currentMonth &&
            d.getFullYear() === currentYear
          ) {
            const dateKey = d.toISOString().split("T")[0];
            if (!map[dateKey]) map[dateKey] = {};
            map[dateKey].suspension = suspension;
          }
        }
      }
    });

    // Map leaves by date range
    leaves.forEach((leave) => {
      if (leave.dateRange && leave.dateRange.length > 0) {
        // Iterate through each group in dateRange
        leave.dateRange.forEach((group) => {
          group.forEach((range) => {
            const [startDateStr, endDateStr] = range;
            const startLeave = new Date(startDateStr)
              .toISOString()
              .split("T")[0];
            const endLeave = new Date(endDateStr).toISOString().split("T")[0];

            for (
              let d = new Date(startLeave);
              d.toISOString().split("T")[0] <= endLeave;
              d.setDate(d.getDate() + 1)
            ) {
              // Only include leaves in the current month/year
              if (
                d.getMonth() === currentMonth &&
                d.getFullYear() === currentYear
              ) {
                const dateKey = d.toISOString().split("T")[0];
                if (!map[dateKey]) map[dateKey] = {};
                map[dateKey].leave = leave;
              }
            }
          });
        });
      }
    });

    // Map manual DTR by selected dates
    manual_dtr.forEach((manualDtr) => {
      if (manualDtr.selectDate && Array.isArray(manualDtr.selectDate)) {
        manualDtr.selectDate.forEach((dateStr) => {
          const dateKey = getDateKey(dateStr);
          const dateObj = new Date(dateKey + "T00:00:00");
          // Only include manual DTR in the current month/year
          if (
            dateObj.getMonth() === currentMonth &&
            dateObj.getFullYear() === currentYear
          ) {
            if (!map[dateKey]) map[dateKey] = {};
            map[dateKey].manual_dtr = manualDtr;
          }
        });
      }
    });

    console.log("Holiday Map:", map); // Debug log
    setHolidayMap(map);
  }, [
    holidays,
    suspensions,
    leaves,
    manual_dtr,
    obLists,
    dailyRec,
    month,
    year,
  ]);

  const isJobOrder = employee?.employmentType?.name === "Job Order";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        margin: "20px",
        fontSize: "12px",
      }}
    >
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead style={{ textAlign: "center" }}>
          <tr>
            <th
              style={{
                border: "1px solid black",
              }}
              rowSpan={2}
            >
              Day
            </th>
            <th colSpan="2" style={{ border: "1px solid black" }}>
              AM
            </th>
            <th colSpan="2" style={{ border: "1px solid black" }}>
              PM
            </th>
            {!watermark && (
              <>
                <th
                  style={{
                    border: "1px solid black",
                  }}
                  rowSpan={2}
                  className="remarks-cell"
                >
                  {isJobOrder ? "REMARKS" : "BASIC REMARKS"}
                </th>
                {!isJobOrder && (
                  <th
                    className="remarks-cell"
                    style={{
                      border: "1px solid black",
                    }}
                    rowSpan={2}
                  >
                    AP REMARKS
                  </th>
                )}
              </>
            )}
          </tr>
          <tr>
            <th style={{ border: "1px solid black" }}>IN</th>
            <th style={{ border: "1px solid black" }}>OUT</th>
            <th style={{ border: "1px solid black" }}>IN</th>
            <th style={{ border: "1px solid black" }}>OUT</th>
          </tr>
        </thead>

        <tbody>
          {Array.from({ length: lastDay }, (_, index) => {
            const day = index + 1;

            const currentDate = new Date(year, month, day);

            const record = dailyRec.find((data) => {
              const recDate = new Date(data.date);
              return (
                recDate.getDate() === day &&
                recDate.getMonth() === Number(month) &&
                recDate.getFullYear() === Number(year)
              );
            });

            // Get holiday data from the map using the formatted date
            // Format date as YYYY-MM-DD without timezone conversion to match the map keys
            const year4digit = year.toString().padStart(4, "0");
            const month2digit = (month + 1).toString().padStart(2, "0");
            const day2digit = day.toString().padStart(2, "0");
            const formattedDate = `${year4digit}-${month2digit}-${day2digit}`;

            let holiday = null;
            if (record?.schedule?.sequence !== "24 HOURS") {
              holiday = holidayMap[formattedDate] || null;
            }

            const lateMinutes = record?.APlateMinutes || 0;
            const undertimeMinutes = record?.APundertimeMinutes || 0;
            const APTotal = lateMinutes + undertimeMinutes;
            return (
              <tr key={day} className="text-center">
                {[
                  `${day} ${currentDate.toLocaleDateString("en-US", {
                    weekday: "short",
                  })}`,
                  record ? record.timeIn.slice(0, -3) : "",
                  record ? record.timeAmOut.slice(0, -3) : "",
                  record ? record.timePmIn.slice(0, -3) : "",
                  record ? record.timeOut.slice(0, -3) : "",
                ].map((item) => (
                  <td
                    style={{ border: "1px solid black", textAlign: "center" }}
                  >
                    {item}
                  </td>
                ))}

                {!watermark && (
                  <>
                    <td
                      className="remarks-cell"
                      style={{ border: "1px solid black", textAlign: "center" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          justifyContent: "center",
                          fontSize: "10px",
                          padding: 0,
                          margin: 0,
                        }}
                      >
                        {record
                          ? record.timeRecStats.map((item) => {
                              const minutesMap = {
                                LATE: record.basicLateMinutes,
                                UNDERTIME: record.basicUndertimeMinutes,
                              };

                              const displayText =
                                item === "Official Business" || item === "LWOP"
                                  ? item
                                  : ["LATE", "UNDERTIME"].includes(item) &&
                                      minutesMap[item]
                                    ? `${item} - ${minutesMap[item]}`
                                    : item;

                              return (
                                <div
                                  className="d-flex justify-content-center p-0 m-0"
                                  key={item}
                                >
                                  {displayText}
                                </div>
                              );
                            })
                          : (() => {
                              return displayHolidays(holiday);
                            })()}
                        {/* {displayHolidays
                          ? displayHolidays(holiday)
                          : record?.timeRecStats?.map((item) => {
                              const minutesMap = {
                                LATE: record.basicLateMinutes,
                                UNDERTIME: record.basicUndertimeMinutes,
                              };

                              const displayText =
                                ["LATE", "UNDERTIME"].includes(item) &&
                                minutesMap[item]
                                  ? `${item} - ${minutesMap[item]}`
                                  : item;

                              return (
                                <div
                                  key={item}
                                  className="d-flex justify-content-center p-0 m-0"
                                >
                                  {displayText}
                                </div>
                              );
                            })} */}
                      </div>
                    </td>

                    {!isJobOrder && (
                      <td
                        className="remarks-cell"
                        style={{
                          border: "1px solid black",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "1rem",
                            justifyContent: "center",
                            fontSize: "10px",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          {record
                            ? (() => {
                                const matchedItem =
                                  record?.printTimeRecStats?.find((item) =>
                                    ["LATE", "UNDERTIME"].includes(item),
                                  );

                                const content =
                                  matchedItem && APTotal > 0
                                    ? `L/U ${APTotal} = ${APConvertions(APTotal)}`
                                    : record?.printTimeRecStats
                                        ?.filter(
                                          (item) =>
                                            !["LATE", "UNDERTIME"].includes(
                                              item,
                                            ),
                                        )
                                        ?.join(", ");

                                return (
                                  <div className="d-flex justify-content-center p-0 m-0">
                                    {content}
                                  </div>
                                );
                              })()
                            : (() => {
                                return displayHolidays(holiday);
                              })()}
                          {/* {displayHolidays?.(holiday) ??
                            (record && (
                              <div className="d-flex justify-content-center p-0 m-0">
                                {(() => {
                                  const matchedItem =
                                    record.printTimeRecStats?.find((item) =>
                                      ["LATE", "UNDERTIME"].includes(item),
                                    );

                                  return matchedItem
                                    ? `L/U ${APTotal} = ${APConvertions(APTotal)}`
                                    : record.printTimeRecStats?.join(", ");
                                })()}
                              </div>
                            ))} */}
                        </div>
                      </td>
                    )}
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
function displayHolidays(holiday) {
  const items = [];
  if (holiday?.holiday) items.push(holiday.holiday.type);
  if (holiday?.leave)
    items.push(getLeaveAbbreviation(holiday.leave.selectedType));
  if (holiday?.suspension) items.push(holiday.suspension.type);
  if (holiday?.manual_dtr) items.push(holiday.manual_dtr.categoryType);
  if (holiday?.ob) items.push("Official Business");

  return items.map((item, index) => (
    <div className="d-flex justify-content-center p-0 m-0" key={index}>
      {item}
    </div>
  ));
}

export default TimeTable;
